/**
 * cardLayout — count-based scaling + staggered offset-grid packing for the
 * character/scene card clusters (KEYSTONE-1256).
 *
 * Coordinate convention: a packer returns slots in a cluster-local space where
 * (0,0) is the cluster's geometric center. Those map into the stage-local X by
 * mirroring left vs right of Muse. Y is relative to the vertical center of the
 * card band.
 *
 * Layout: dual (centered) clusters, 9° max rotation, brick offset 0.18·pitch,
 * ±1.8px jitter, nearest-center ordering.
 */

/* ── Constants ── */
const ASPECT = 1.2;                 // card height = 1.2 × width (Wu)
const LABEL_FRAC = 80 / 96;         // Gu
const PITCH_COEFF = (1 + LABEL_FRAC) / 2; // qu ≈ 0.91667
const CARD_GAP = 12;                // centered-layout gap baked into pitch (Zu)
const ROT_DEG = 9;                  // max ± rotation and the skew baked into pitch
const JITTER = 12 * 0.3;            // ±1.8px positional jitter magnitude
const BRICK = 0.18;                 // alternating-row offset as a fraction of pitch
// Center-to-center spacing as a fraction of the base pitch. <1 packs the
// cards closer so neighbours overlap; >1 spreads them apart. Tuned to 1.05 so
// cards sit just clear of each other (a small gap, no overlap) at the approved
// design density.
const DENSITY = 1.05;
const MIN_SIZE = 48;
const MAX_SIZE = 288;
const MUSE_CLEARANCE_PAD = 32;      // gap between Muse edge and the nearest card
const EDGE_MARGIN = 16;             // gap between the outermost card and the screen edge
const SINGLE_CARD_FACTOR = 1.12;    // a lone card is at most this × the two-card size
const STAGE_H_MIN = 48;
const STAGE_H_MAX = 560;

// Discrete size ceiling by count (index = count-1); 15+ uses the tail (107).
// The 2-card ceiling is capped at 224 (not 288) so two low-pressure cards don't
// balloon; the 1-card case is handled at runtime in layoutCluster (derived from
// the two-card size) since its ceiling here would otherwise pin it to MAX_SIZE.
const SIZE_TABLE = [224, 224, 224, 170, 134, 134, 134, 131, 127, 124, 120, 117, 114, 110, 107];

export interface CardSlot {
  /** Stage-local X (0 = stage horizontal center, +/- = right/left). */
  x: number;
  /** Band-local Y (0 = vertical center of the card band). */
  y: number;
  /** Rotation in degrees. */
  rotation: number;
  /** Stacking order (higher = on top). */
  z: number;
}

export interface ClusterLayout {
  slots: CardSlot[];
  /** Final card width in px (height = cardW × ASPECT). */
  cardW: number;
}

/* ── Seeded RNG (deterministic jitter — same seed yields the same layout) ── */

/** djb-ish string hash → positive 31-bit int. */
function hashString(s: string): number {
  let t = 0;
  for (let i = 0; i < s.length; i++) {
    t = (t * 31 + s.charCodeAt(i)) & 0x7fffffff;
  }
  return t;
}

/** MINSTD (Lehmer) LCG seeded from `seed`, yielding values in [0,1). */
function seededRng(seed: number): () => number {
  let t = seed % 2147483647;
  if (t <= 0) t += 2147483646;
  return () => {
    t = (t * 16807) % 2147483647;
    return (t - 1) / 2147483646;
  };
}

/* ── Size + pitch ── */

/** Size ceiling for a given card count (Ku). */
function sizeForCount(count: number): number {
  if (count <= 0) return MAX_SIZE;
  const t = count <= SIZE_TABLE.length ? SIZE_TABLE[count - 1] : SIZE_TABLE[SIZE_TABLE.length - 1];
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, t));
}

/** Slack added to pitch by the ±9° rotation, proportional to card height (Xu). */
function rotationSlack(size: number): number {
  return Math.sin((ROT_DEG * Math.PI) / 180) * ((size * ASPECT) / 2);
}

/**
 * Half the real horizontal footprint of a card at the band edge: a card's
 * rotated bounding box is wider than `size`, plus the slot can be pushed out by
 * the brick offset (0.18·pitch) and jitter. The band's near/far edges only know
 * the slot center, so we must reserve this much so the card body stays inside
 * the clearance / screen edge instead of spilling over Muse or off-screen.
 */
function halfFootprint(size: number): number {
  const rad = (ROT_DEG * Math.PI) / 180;
  const rotatedHalfW = (size * Math.cos(rad) + size * ASPECT * Math.sin(rad)) / 2;
  return rotatedHalfW + BRICK * pitch(size) + JITTER / 2;
}

/**
 * Centered-layout pitch: center-to-center spacing (Zu), scaled by DENSITY to
 * tune how tightly neighbouring cards pack (see DENSITY).
 */
function pitch(size: number): number {
  return (PITCH_COEFF * size + CARD_GAP + rotationSlack(size)) * DENSITY;
}

/* ── Band descriptor: a packer is given a function size→band ── */

interface Band {
  /** Center X of the available band (stage-local). */
  centerX: number;
  /** Available width of the band at this card size. */
  width: number;
}

/**
 * The stage-local band a cluster's cards may occupy at a given card size. The
 * near edge faces Muse (reserving museW/2 + MUSE_CLEARANCE_PAD), the far edge is
 * the screen side (reserving EDGE_MARGIN); both reserve the card's real rotated
 * half-footprint so card bodies stay inside the clearance and the screen bounds.
 * Single source of truth for the band geometry — used by both the packer and the
 * Bunch-focus pile centering.
 */
function computeBand(side: "left" | "right", originX: number, museW: number, size: number): Band {
  const clearance = museW / 2 + MUSE_CLEARANCE_PAD;
  const hf = halfFootprint(size);
  const dir = side === "left" ? 1 : -1;
  let near: number;
  let far: number;
  if (side === "left") {
    near = originX - clearance - hf;
    far = -originX + hf + EDGE_MARGIN;
  } else {
    near = -originX + clearance + hf;
    far = originX - hf - EDGE_MARGIN;
  }
  // Signed available width (dir orients near→far per side). Clamp to 0 so an
  // inverted band (card too big for the section) reads as zero room and the size
  // search shrinks, rather than masking it as a wide band.
  return { centerX: (near + far) / 2, width: Math.max(dir * (near - far), 0) };
}

/**
 * Centered/dual packer. Packs `count` cards into a staggered offset grid
 * within the band returned by `bandFor(size)`, filling outward from center.
 *
 * @param bandFor   size → { centerX, width } for the cluster's side
 * @param stageH    available vertical height (clamped 48..560)
 * @param count     number of cards
 * @param dir       +1 left cluster, -1 right cluster (mirrors X about centerX)
 * @param maxCardW  size ceiling override
 * @param seedPrefix RNG seed prefix so the two clusters can differ if desired
 */
/**
 * Largest card size (from the count ceiling down) whose grid can hold all cards
 * in the band's available width AND height. Uses the full stage area — in a
 * wide/short band it adds rows rather than shrinking cards into one long row.
 * Shared by packCentered and the lone-card cap so neither re-runs the other.
 */
function resolveSize(
  bandFor: (size: number) => Band,
  stageH: number,
  count: number,
  maxCardW: number,
): number {
  const colsFor = (size: number) =>
    Math.max(1, Math.floor(bandFor(size).width / pitch(size)) + 1);
  const rowsFor = (size: number) =>
    Math.max(1, Math.floor(Math.max(stageH - size * ASPECT, 0) / pitch(size)) + 1);

  const startSize = Math.min(sizeForCount(count), maxCardW, MAX_SIZE);
  let size = MIN_SIZE;
  for (let s = startSize; s >= MIN_SIZE; s -= 2) {
    size = s;
    if (colsFor(s) * rowsFor(s) >= count) break;
  }
  return size;
}

function packCentered(
  bandFor: (size: number) => Band,
  stageH: number,
  count: number,
  dir: number,
  maxCardW: number,
  seedPrefix: string,
): ClusterLayout {
  if (count <= 0) return { slots: [], cardW: MAX_SIZE };

  // How many columns/rows fit a given card size in the band's width/height.
  const colsFor = (size: number) =>
    Math.max(1, Math.floor(bandFor(size).width / pitch(size)) + 1);
  const rowsFor = (size: number) =>
    Math.max(1, Math.floor(Math.max(stageH - size * ASPECT, 0) / pitch(size)) + 1);

  const size = resolveSize(bandFor, stageH, count, maxCardW);

  // Distribute over as many rows as the chosen size allows so the cards fill the
  // height instead of clinging to the top, then derive the columns to match.
  const maxRowsAtSize = rowsFor(size);
  const maxColsAtSize = colsFor(size);
  let rows = Math.min(maxRowsAtSize, count);
  let cols = Math.ceil(count / rows);
  if (cols > maxColsAtSize) {
    // Too few rows to fit width-wise: add rows back until columns fit.
    cols = maxColsAtSize;
    rows = Math.min(maxRowsAtSize, Math.ceil(count / cols));
  }

  const p = pitch(size);
  const { centerX } = bandFor(size);
  const capacity = rows * cols;
  const placed = Math.min(count, capacity);
  const basePerRow = Math.floor(placed / rows);
  const extra = placed % rows;
  const totalY = (rows - 1) * p;

  // Build slots row by row; brick-offset alternating rows; sort nearest-center.
  const raw: { localX: number; y: number; d: number }[] = [];
  for (let r = 0; r < rows; r++) {
    const inRow = Math.min(cols, basePerRow + (r < extra ? 1 : 0));
    if (inRow <= 0) continue;
    const rowW = (inRow - 1) * p;
    const brick = rows > 1 ? (r % 2 === 0 ? -1 : 1) * p * BRICK : 0;
    const startX = -rowW / 2 + brick;
    const y = -totalY / 2 + r * p;
    for (let c = 0; c < inRow; c++) {
      const localX = startX + c * p;
      raw.push({ localX, y, d: localX * localX + y * y });
    }
  }
  raw.sort((a, b) => a.d - b.d);

  const slots: CardSlot[] = raw.slice(0, count).map((slot, i) => {
    const rng = seededRng(hashString(`${seedPrefix}-${i}`));
    const jx = slot.localX + (rng() - 0.5) * JITTER;
    const jy = slot.y + (rng() - 0.5) * JITTER;
    const rot = (rng() - 0.5) * 2 * ROT_DEG;
    return {
      x: centerX + dir * jx,
      y: jy,
      rotation: dir * rot,
      z: Math.round(1000 + jy),
    };
  });

  return { slots, cardW: size };
}

/**
 * Dual/centered cluster layout. Places a cluster on one side of Muse.
 *
 * @param side      "left" (characters) or "right" (scenes/places)
 * @param originX   half the stage width (distance from center to edge)
 * @param stageH    band height
 * @param museW     Muse orb width (clearance = museW/2 + 20)
 * @param count     number of cards
 * @param maxCardW  size ceiling (default 288)
 */
export function layoutCluster(
  side: "left" | "right",
  originX: number,
  stageH: number,
  museW: number,
  count: number,
  maxCardW: number = MAX_SIZE,
): ClusterLayout {
  if (originX === 0 || count === 0) return { slots: [], cardW: MAX_SIZE };
  const dir = side === "left" ? 1 : -1;
  const h = Math.max(Math.min(stageH, STAGE_H_MAX), STAGE_H_MIN);

  const bandFor = (size: number): Band => computeBand(side, originX, museW, size);

  // A lone card has no width pressure, so the size search would pin it to the
  // count ceiling and dwarf the two-card layout. Cap it just above whatever the
  // two-card size actually resolves to (runtime band-dependent), so "1 card" is
  // only slightly bigger than "2 cards" on any screen.
  if (count === 1) {
    const twoCardW = resolveSize(bandFor, h, 2, maxCardW);
    maxCardW = Math.min(maxCardW, Math.round(twoCardW * SINGLE_CARD_FACTOR));
  }

  return packCentered(bandFor, h, count, dir, maxCardW, `slot-${side}`);
}

export const CARD_ASPECT = ASPECT;

/**
 * Approximate stage-local center X of a cluster's band, used to keep the Bunch
 * focus pile on the cluster's own side of Muse. Mirrors the band math in
 * layoutCluster at a representative card size.
 */
export function clusterCenterX(
  side: "left" | "right",
  originX: number,
  museW: number,
  cardW: number,
): number {
  return computeBand(side, originX, museW, cardW).centerX;
}

/* ── Bunch focus mode ── */

const FOCUS_SCALE = 1.4;
const UNFOCUS_SCALE = 0.7;
// Bunch scatter configuration.
const BUNCH = {
  spread: 208,
  scatterX: 62,
  drop: 16,
  scatter: 300,
  fan: 58,
  scaleVar: 0.3,
} as const;

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export interface FocusTransform {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  z: number;
}

/**
 * Bunch focus transform for one card while a card in its cluster is selected:
 * the focused card scales up at center, the rest scatter into a pile behind it.
 *
 * @param id        card id (seeds the deterministic scatter)
 * @param isSelected whether this is the focused card
 * @param pileIndex this card's index among the non-selected cards (for side parity)
 */
export function bunchFocus(id: string, isSelected: boolean, pileIndex: number): FocusTransform {
  if (isSelected) {
    return { x: 0, y: 0, rotation: 0, scale: FOCUS_SCALE, z: 100000 };
  }
  const rng = seededRng(hashString(id));
  const r3 = () => (rng() + rng() + rng()) / 3; // ~gaussian in [0,1]
  const side = pileIndex % 2 === 0 ? 1 : -1;
  const ang = (r3() - 0.5) * 2.6;
  const mag = 0.6 + 0.4 * r3();
  const rotJit = r3() - 0.5;
  const scl = r3();
  const x = side * Math.cos(ang) * (BUNCH.spread / 2) * mag + (r3() - 0.5) * BUNCH.scatterX;
  const y = BUNCH.drop + Math.sin(ang) * (BUNCH.scatter / 2) * mag + (r3() - 0.5) * (BUNCH.scatterX * 0.5);
  const halfSpread = Math.max(BUNCH.spread / 2, 1);
  const rotation = clamp(x / halfSpread, -1, 1) * BUNCH.fan * 0.5 + rotJit * BUNCH.fan * 0.3;
  const scale = UNFOCUS_SCALE + scl * BUNCH.scaleVar;
  // Piled cards: nearer the center sit on top (z = 50000 - min(dist, 9000)).
  const z = Math.round(50000 - Math.min(Math.hypot(x, y), 9000));
  return { x, y, rotation, scale, z };
}
